import { backendApi } from "@/services/backend/api";
import {
  LabData,
  Report,
  VDI6022,
} from "@/services/backend/htz/report/types/report";
import { Filter } from "@/shared/lib/filter/filter";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import {
  downloadResponseHandler,
  previewResponseHandler,
} from "@/services/backend/_lib/pdf-helpers";
import { Address } from "@/services/backend/addresses/types/address";
import { Result } from "@/services/backend/htz/inspection/components/inspection-result";
import { Kind } from "@/services/backend/samples/types/kind";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Lst } from "@/shared/lib/lst/lst";
import { UUID } from "@bitory-gmbh/nidavellir";
import { updateCache } from "@/services/backend/_lib/update-cache";

export const htzReportApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["HtzReport"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      htzReportCreate: build.mutation<Report, CreateRequest>({
        query: (request) => ({
          url: "/htz/report",
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          "HtzReport",
        ],
      }),
      htzReportShow: build.query<Report, ShowRequest>({
        query: (request) => ({
          url: "/htz/report",
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("HtzReport"),
      }),
      htzReportPdfPreview: build.query<{ objectURL: string }, PDFRequest>({
        query: (request) => ({
          url: "/htz/report",
          method: "GET",
          params: request,
          headers: {
            Accept: "application/pdf",
          },
          responseHandler: previewResponseHandler,
          // IMPORTANT. Since this query will download the PDF under no circumstances
          // should the results be cached. This could end up using the users memory.
          cache: "no-cache",
        }),
        providesTags: (_r, _e, request) => [
          { type: "HtzReport", id: request.reportId },
        ],
      }),
      htzReportPdfDownload: build.mutation<
        { filename: string; objectURL: string },
        PDFRequest
      >({
        query: (request) => ({
          url: "/htz/report",
          method: "GET",
          params: { ...request, download: true },
          headers: { Accept: "application/pdf" },
          responseHandler: downloadResponseHandler,
          // IMPORTANT. Since this query will download the PDF under no circumstances
          // should the results be cached. This could end up using the users memory.
          cache: "no-cache",
        }),
      }),
      htzReportList: build.query<Lst<Report>, ListRequest>({
        query: (request) => ({
          url: "/htz/report/list",
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: (result) => providesList(result?.items, "HtzReport"),
      }),
      htzReportPatch: build.mutation<Report, PatchRequest>({
        query: (request) => ({
          url: "/htz/report",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          "HtzReport",
        ],
      }),
      htzReportSetImmutable: build.mutation<Report, SetImmutableRequest>({
        query: (request) => ({
          url: "/htz/report/immutable",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          "HtzReport",
        ],
      }),
      htzReportCreateItem: build.mutation<Report, CreateItemRequest>({
        query: (request) => ({
          url: "/htz/report/item",
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          "HtzReport",
        ],
      }),
      htzReportPatchItem: build.mutation<Report, PatchItemRequest>({
        query: (request) => ({
          url: "/htz/report/item",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          "HtzReport",
        ],
      }),
      htzReportRemoveItem: build.mutation<Report, RemoveItemRequest>({
        query: (request) => ({
          url: "/htz/report/item",
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          "HtzReport",
        ],
      }),
      htzReportAddAssessment: build.mutation<Report, AddAssessmentRequest>({
        query: (request) => ({
          url: "/htz/report/assessment",
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          "HtzReport",
        ],
      }),
      htzReportRemoveAssessment: build.mutation<
        Report,
        RemoveAssessmentRequest
      >({
        query: (request) => ({
          url: "/htz/report/assessment",
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          "HtzReport",
        ],
      }),
      htzReportUpdateAssessmentResult: build.mutation<
        Report,
        UpdateAssessmentResultRequest
      >({
        query: (request) => ({
          url: "/htz/report/assessment-result",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          "HtzReport",
        ],
      }),
      htzReportPatchMicrobialAssessment: build.mutation<
        Report,
        PatchMicrobialAssessmentRequest
      >({
        query: (request) => ({
          url: "/htz/report/assessment/microbial",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          "HtzReport",
        ],
      }),
      htzReportUpdateMicrobialAssessmentSample: build.mutation<
        Report,
        UpdateMicrobialAssessmentSampleRequest
      >({
        query: (request) => ({
          url: "/htz/report/assessment/microbial/sample",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          "HtzReport",
        ],
      }),
      htzReportPatchPhoto: build.mutation<Report, PatchPhotoRequest>({
        query: (request) => ({
          url: "/htz/report/assessment/photo",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          "HtzReport",
        ],
      }),
      htzReportPatchStructuralAssessment: build.mutation<
        Report,
        PatchStructuralAssessmentRequest
      >({
        query: (request) => ({
          url: "/htz/report/assessment/structural",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          "HtzReport",
        ],
      }),
      htzReportPatchVisualAssessment: build.mutation<
        Report,
        PatchVisualAssessmentRequest
      >({
        query: (request) => ({
          url: "/htz/report/assessment/visual",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          "HtzReport",
        ],
      }),
      htzReportDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: "/htz/report",
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          "HtzReport",
        ],
      }),
    }),
  });

export const {
  useHtzReportCreateMutation,
  useHtzReportShowQuery,
  useHtzReportPdfPreviewQuery,
  useHtzReportPdfDownloadMutation,
  useHtzReportListQuery,
  useHtzReportPatchMutation,
  useHtzReportSetImmutableMutation,
  useHtzReportCreateItemMutation,
  useHtzReportPatchItemMutation,
  useHtzReportRemoveItemMutation,
  useHtzReportAddAssessmentMutation,
  useHtzReportRemoveAssessmentMutation,
  useHtzReportUpdateAssessmentResultMutation,
  useHtzReportPatchMicrobialAssessmentMutation,
  useHtzReportUpdateMicrobialAssessmentSampleMutation,
  useHtzReportPatchPhotoMutation,
  useHtzReportPatchStructuralAssessmentMutation,
  useHtzReportPatchVisualAssessmentMutation,
  useHtzReportDeleteMutation,
} = htzReportApi;

export interface CreateRequest {
  id: string;
  processId: UUID;
  companyId: UUID;
  branchId: UUID;
  customerId: UUID;
  serviceObjectId: UUID;
}

export interface ShowRequest {
  id: string;
}

export interface PDFRequest {
  reportId: string;
  document: ReportDocument;
  download?: boolean;
}

export enum ReportDocument {
  ALL = "all",
  REPORT = "report",
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters {
  orderBy?: OrdrBy<string>[];
  reportDate?: Filter<string>;
  immutable?: Filter<boolean>;
  limit?: number;
  offset?: number;
}

// PatchRequest updates the report. All fields except `id` are optional:
// an omitted key leaves the corresponding value unchanged (the backend
// `patch.Field` is "unset" when the JSON key is absent).
export interface PatchRequest {
  id: string;
  date?: string | null;
  createdBy?: string;
  createdByCompany?: Address;
  objectAddress?: Address;
  createdFor?: Address;
  laboratories?: LabData[];
  inspectionDate?: string | null;
  inspectionConductedBy?: string;
  inspectionConductedByStaff?: string;
  lastInspectionDate?: string | null;
  nextInspectionDate?: string | null;
  vdi6022Judgement?: VDI6022;
  ahuImageFileID?: string;
}

export interface CreateItemRequest {
  id: string;
  itemId: string;
  order: number;
  componentId: string;
}

// PatchItemRequest updates an item. `order` and `recommendations` are
// optional; an omitted key leaves the value unchanged.
export interface PatchItemRequest {
  id: string;
  itemId: string;
  order?: number;
  recommendations?: string[];
}

export interface RemoveItemRequest {
  id: string;
  itemId: string;
}

export type AssessmentType = "microbial" | "photo" | "structural" | "visual";

export interface AddAssessmentRequest {
  id: string;
  type: AssessmentType;
  assessmentId: string;
  componentId: string;
}

export interface RemoveAssessmentRequest {
  id: string;
  assessmentId: string;
}

export interface UpdateAssessmentResultRequest {
  id: string;
  assessmentId: string;
  result: Result;
}

// PatchMicrobialAssessmentRequest patches a microbial assessment. Patch
// fields are optional; an omitted key leaves the value unchanged.
export interface PatchMicrobialAssessmentRequest {
  id: string;
  assessmentId: string;
  componentId?: string;
  conductedAt?: string;
  result?: Result;
}

export interface UpdateMicrobialAssessmentSampleRequest {
  id: string;
  assessmentId: string;
  sampleKind: Kind;
  sampleId: string;
}

export interface PatchPhotoRequest {
  id: string;
  assessmentId: string;
  componentId?: string;
  comment?: string;
  fileId?: string;
  conductedAt?: string;
}

export type PatchStructuralAssessmentRequest = {
  id: string;
  assessmentId: string;
  componentId?: string;
  requirement?: string;
  deficiency?: string;
  recommendation?: string;
  comment?: string;
  conductedAt?: string;
  result?: Result;
};

export interface PatchVisualAssessmentRequest {
  id: string;
  assessmentId: string;
  componentId?: string;
  deficiency?: string;
  recommendation?: string;
  comment?: string;
  conductedAt?: string;
  result?: Result;
}

export interface SetImmutableRequest {
  id: string;
  reason: string;
  setBy: string;
}

export enum ReportImmutabilityReason {
  SetByUser = "set_by_user",
}

export interface DeleteRequest {
  id: string;
}

/**
 * Patches the cached report for an optimistic update. Defined here, at module
 * scope, so it keeps one identity for the whole app — see {@link updateCache}.
 */
export const updateHtzReportCache = updateCache(htzReportApi, "htzReportShow");
